import React, { useEffect, useRef, useState } from "react";
import { useAppConfig } from "@/appConfig";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { trackEvent } from "@/services/analytics";
import ShareCard from "../MemberWantAd/v2/ShareCard";
import {
  gitchaExplainerUrl,
  trackGitchaExplainerClick,
} from "@/helpers/gitchaExplainer";
import "./GitchaSyndication.scss";

const UNLOCK_MS = 2200;

const Padlock = ({ open }) => (
  <svg viewBox="0 0 24 24" className="gitcha-padlock" aria-hidden="true">
    <path
      className={`gitcha-padlock__shackle${open ? " is-open" : ""}`}
      d="M8 11V8a4 4 0 0 1 8 0v3"
    />
    <rect x="5" y="11" width="14" height="10" rx="2.5" />
  </svg>
);

const GitchaSyndication = ({
  wantAdId,
  isSyndicated,
  adDetails,
  shareUrl,
  fbUrl,
  imageUrl,
  isWantAdCreated,
  onShowOnGitcha,
  posterPlan,
  shareStatus,
  onRetryShare,
  surface = "want_listing_details",
}) => {
  const { flags } = useAppConfig();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const live = !!isSyndicated;
  const wasLive = useRef(live);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    if (live && !wasLive.current) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      wasLive.current = true;
      if (reduced) return;
      setUnlocking(true);
      const timer = setTimeout(() => setUnlocking(false), UNLOCK_MS);
      return () => clearTimeout(timer);
    }
    wasLive.current = live;
  }, [live]);

  if (!flags?.mlsAgentEditor || isGitchaDefaultWebsite) return null;

  return (
    <div
      className={`share-card-wrapper share-sidecard mb-4 gitcha-share${live ? " is-live" : " is-locked"}${unlocking ? " is-unlocking" : ""}`}
    >
      {!live && (
        <div className="gitcha-share__intro">
          <h4>Syndicate to unlock expanded sharing&nbsp;capabilities</h4>
          <p>Visible to members only until you syndicate to&nbsp;Gitcha.com.</p>
        </div>
      )}

      <div className="gitcha-share__card">
        <ShareCard
          wantAdId={wantAdId}
          isSideCard
          hideHeader={!live}
          locked={!live}
          adDetails={adDetails}
          isWantAdCreated={isWantAdCreated}
          shareLink={live ? shareUrl : ""}
          fbLink={live ? fbUrl : ""}
          imageLink={imageUrl}
          fromWantAdDetails
          posterPlan={posterPlan}
          shareStatus={shareStatus}
          onRetryShare={onRetryShare}
        />
        {(!live || unlocking) && (
          <div className="gitcha-share__veil">
            <span className="gitcha-share__badge">
              <Padlock open={live} />
            </span>
          </div>
        )}
      </div>

      {!live && (
        <button
          type="button"
          className="btn btn-primary w-100 gitcha-share__btn"
          onClick={() => {
            trackEvent({
              name: "gitcha_show_on_gitcha_clicked",
              attributes: { wantAdId, surface },
            });
            onShowOnGitcha();
          }}
        >
          Syndicate to&nbsp;Gitcha.com
        </button>
      )}

      <a
        className="gitcha-share__explainer"
        href={gitchaExplainerUrl(live ? "share_card" : "share_card_unpublished")}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackGitchaExplainerClick(live ? "share_card" : "share_card_unpublished")
        }
      >
        How Gitcha.com works for buyer&nbsp;agents&nbsp;&rarr;
      </a>
    </div>
  );
};

export default GitchaSyndication;
